import { Link, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import { useI18n } from "../lib/i18n";
import api, { errorMessage } from "../lib/api";
import { Sparkles, User, LogOut, History, Shield, CreditCard, Camera, Loader2, MessageSquareCode, Library, Images, Wand2, Menu } from "lucide-react";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import { toast } from "sonner";

export default function AppShell() {
  const { user, logout, checkAuth } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openTickets, setOpenTickets] = useState(0);

  const isAdmin = user?.role === "admin";

  // Poll open-ticket count for admin badge every 60s
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/support/tickets/open-count");
        if (!cancelled) setOpenTickets(data.open || 0);
      } catch { /* silent */ void 0; }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin]);

  const nav = [
    { to: "/app", label: t("generator"), icon: Sparkles, key: "gen" },
    { to: "/app/studio", label: t("nav_studio"), icon: Wand2, key: "studio" },
    { to: "/app/gallery", label: t("nav_gallery") || "Gallery", icon: Images, key: "gallery" },
    { to: "/app/history", label: t("history_title"), icon: History, key: "hist" },
    { to: "/app/text-assistant", label: t("nav_text_assistant") || "AI Prompt Assistant", icon: MessageSquareCode, key: "txt" },
    { to: "/app/text-library", label: t("nav_text_library") || "Assistant Library", icon: Library, key: "txtlib" },
    { to: "/app/account", label: t("account"), icon: CreditCard, key: "acc" },
  ];
  if (isAdmin) nav.push({ to: "/app/admin", label: t("admin"), icon: Shield, key: "adm", badge: openTickets });

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same file
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t("file_too_large") || "Max 2 MB"); return; }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await api.post("/account/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("avatar_updated") || "Avatar updated");
      await checkAuth?.();
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setUploading(false); }
  };

  const removeAvatar = async () => {
    setUploading(true);
    try {
      await api.delete("/account/avatar");
      toast.success(t("avatar_removed") || "Avatar removed");
      await checkAuth?.();
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setUploading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0E0E12] text-white flex">
      <aside className="hidden md:flex w-64 flex-col border-r border-white/10 bg-[#121218] p-6">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="brand-link">
          <img src="/logo-mark.png" alt="Vixelforge" className="w-8 h-8 shrink-0" />
          <span className="font-black text-xl tracking-tight">Vixelforge</span>
        </Link>
        <nav className="flex-1 space-y-1">
          {nav.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`nav-${item.key}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  active
                    ? "bg-orange-500/10 text-orange-400 border border-orange-500/20"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span
                    data-testid={`nav-badge-${item.key}`}
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black tabular-nums"
                    title={t("open_tickets_count") || `${item.badge} open tickets`}
                  >
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="mb-4"><LanguageSwitcher /></div>
        <div className="pt-6 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="relative group shrink-0">
              <button
                type="button"
                onClick={onPickFile}
                disabled={uploading}
                data-testid="avatar-upload-btn"
                title={t("change_avatar") || "Change avatar"}
                className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden border border-white/10 hover:border-orange-500/60 focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 text-zinc-300 animate-spin" />
                ) : user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-4 h-4 text-zinc-400" />
                )}
                <span className="absolute inset-0 rounded-full bg-[#0E0E12]/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                  <Camera className="w-3.5 h-3.5 text-white" />
                </span>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={onFileChange}
                data-testid="avatar-file-input"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold" data-testid="user-plan">
                {user?.plan}
              </div>
              {user?.picture && (
                <button
                  type="button"
                  onClick={removeAvatar}
                  data-testid="avatar-remove-btn"
                  className="text-[10px] text-zinc-500 hover:text-red-400 mt-0.5"
                >
                  {t("remove_avatar") || "Remove photo"}
                </button>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start mt-2 text-zinc-400 hover:text-white hover:bg-white/5"
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="md:hidden flex items-center justify-between p-4 border-b border-white/10 bg-[#121218]">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="Vixelforge" className="w-7 h-7 shrink-0" />
            <span className="font-black tracking-tight">Vixelforge</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button className="p-2 rounded-md border border-white/10 text-zinc-200 hover:bg-white/5" data-testid="mobile-menu-btn" aria-label="Menu">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#121218] border-white/10 text-white w-[82vw] max-w-xs p-0 flex flex-col" data-testid="mobile-menu">
                <SheetTitle className="px-5 pt-5 pb-3 text-left text-base font-black">Vixelforge</SheetTitle>
                <nav className="flex-1 overflow-y-auto px-3 space-y-1">
                  {nav.map((item) => {
                    const Icon = item.icon;
                    const active = location.pathname === item.to;
                    return (
                      <Link key={item.key} to={item.to} onClick={() => setMenuOpen(false)} data-testid={`mnav-${item.key}`}
                        className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${active ? "bg-orange-500/10 text-orange-400 border border-orange-500/20" : "text-zinc-300 hover:bg-white/5"}`}>
                        <Icon className="w-4 h-4" />
                        <span className="flex-1">{item.label}</span>
                        {item.badge > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black inline-flex items-center justify-center">{item.badge}</span>}
                      </Link>
                    );
                  })}
                </nav>
                <div className="border-t border-white/10 p-4">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-2">{user?.plan}</div>
                  <Button variant="ghost" size="sm" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-white/5"
                    onClick={async () => { setMenuOpen(false); await logout(); navigate("/"); }} data-testid="mobile-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
